import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AssetCategory } from "@/types/asset";
import { Package, Monitor, Key, FileText, Briefcase, Car, Shield, Settings } from "lucide-react";

interface AssetCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: AssetCategory | null;
  onSuccess?: () => void;
}

export const AssetCategoryDialog = ({
  open,
  onOpenChange,
  category,
  onSuccess
}: AssetCategoryDialogProps) => {
  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    category_type: "",
    asset_type: "physical" as "physical" | "digital" | "credentials",
    description: "",
    requires_serial: false,
    requires_return: true,
    color: "#6366f1",
    icon: "Package"
  });
  
  const [isLoading, setIsLoading] = useState(false);
  
  // Common asset category types
  const categoryTypes = [
    "أجهزة كمبيوتر",
    "أجهزة محمولة",
    "أثاث مكتبي",
    "معدات مكتبية",
    "سيارات",
    "برامج",
    "تراخيص",
    "بطاقات دخول",
    "مفاتيح",
    "معدات أمان",
    "أخرى"
  ];
  
  const iconOptions = [
    { value: "Package", label: "صندوق", icon: Package },
    { value: "Monitor", label: "شاشة", icon: Monitor },
    { value: "Key", label: "مفتاح", icon: Key },
    { value: "FileText", label: "ملف", icon: FileText },
    { value: "Briefcase", label: "حقيبة", icon: Briefcase },
    { value: "Car", label: "سيارة", icon: Car },
    { value: "Shield", label: "درع", icon: Shield },
    { value: "Settings", label: "إعدادات", icon: Settings }
  ];
  
  useEffect(() => {
    if (category) {
      setFormData({
        name_ar: category.name_ar || "",
        name_en: category.name_en || "",
        category_type: category.category_type || "",
        asset_type: category.asset_type || "physical",
        description: category.description || "",
        requires_serial: category.requires_serial || false,
        requires_return: category.requires_return || true,
        color: category.color || "#6366f1",
        icon: category.icon || "Package"
      });
    } else {
      setFormData({
        name_ar: "",
        name_en: "",
        category_type: "",
        asset_type: "physical",
        description: "",
        requires_serial: false,
        requires_return: true,
        color: "#6366f1",
        icon: "Package"
      });
    }
  }, [category]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name_ar || !formData.category_type) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (category) {
        // Update existing category - remove non-updatable fields
        const { created_at, updated_at, id, ...updateData } = formData as any;
        const { error } = await supabase
          .from("asset_categories")
          .update(updateData)
          .eq("id", category.id);
        
        if (error) throw error;
        toast.success("تم تحديث التصنيف بنجاح");
      } else {
        // Create new category
        const { error } = await supabase
          .from("asset_categories")
          .insert([formData]);
        
        if (error) throw error;
        toast.success("تم إضافة التصنيف بنجاح");
      }
      
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error("حدث خطأ أثناء حفظ التصنيف");
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>
            {category ? "تعديل التصنيف" : "إضافة تصنيف جديد"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name_ar">اسم التصنيف (عربي) *</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="name_en">اسم التصنيف (إنجليزي)</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="category_type">نوع التصنيف *</Label>
            <Select
              value={formData.category_type}
              onValueChange={(value) => setFormData({ ...formData, category_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="اختر نوع التصنيف" />
              </SelectTrigger>
              <SelectContent>
                {categoryTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="asset_type">نوع العهدة</Label>
            <Select
              value={formData.asset_type}
              onValueChange={(value: "physical" | "digital" | "credentials") => 
                setFormData({ ...formData, asset_type: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="physical">مادية</SelectItem>
                <SelectItem value="digital">رقمية</SelectItem>
                <SelectItem value="credentials">بيانات دخول</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="icon">الأيقونة</Label>
            <Select
              value={formData.icon}
              onValueChange={(value) => setFormData({ ...formData, icon: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {iconOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <option.icon className="w-4 h-4" />
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="color">اللون</Label>
            <div className="flex gap-2">
              <Input
                id="color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-20"
              />
              <Input
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="flex-1"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="description">الوصف / ملاحظات</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="أدخل وصف أو ملاحظات عن هذا التصنيف..."
            />
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="requires_serial">يتطلب رقم تسلسلي</Label>
              <Switch
                id="requires_serial"
                checked={formData.requires_serial}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, requires_serial: checked })
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="requires_return">يتطلب إرجاع</Label>
              <Switch
                id="requires_return"
                checked={formData.requires_return}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, requires_return: checked })
                }
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="flex-1"
            >
              إلغاء
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? "جاري الحفظ..." : category ? "تحديث" : "إضافة"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};